import React, {useState, useEffect, useRef} from 'react'
import { useNavigate } from "react-router-dom";
import { useForm } from 'react-hook-form'
import { Box, Stack, Tabs, Tab, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled, useTheme } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { GridToolbarContainer } from '@mui/x-data-grid';
import { GridToolbarColumnsButton } from '@mui/x-data-grid';
import { GridToolbarFilterButton } from '@mui/x-data-grid';
import { GridToolbarDensitySelector } from '@mui/x-data-grid';
import { GridToolbarExport } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import dayjs from "dayjs";
import 'dayjs/locale/ja'
import { userURL, apiURL } from './App';
import { authJson } from './apiClient';
import { PrimaryButton, SecondButton, BlackButton, ThemeInputText } from './Controls';
import { SelectDate } from './SelectDate';
import { CheckSelect } from './CheckSelect';
import { toDateString } from './Controls';

// 検索リストツールバー
const SearchListToolbar = () => {
  let timestamp = dayjs().format('YYYYMMDDHHmmss')
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport csvOptions={{ fileName: `memos_${timestamp}.csv`, utf8WithBom: true }}/>
    </GridToolbarContainer>
  );
}

// コンテナ
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: '#fff',
  position: 'absolute',
  padding: '20px',
  width: '100%',
  height: '100%'
});

// カード
export const Card = styled('div')({
  backgroundColor: '#fff',
  borderRadius: '10px',
  boxShadow: '0 0 10px rgba(0,0,0,0.1)',
  padding: '20px',
  margin: '20px 20px 0px 20px',
  width: '100%',
});

// タブパネル
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="panel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && 
        <Container>{children}</Container>}
    </div>
  );
}

// データグリッド
export const ThemeDataGrid = styled(DataGrid)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
  },
  // 列メニュー（…）を完全に非表示
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  // ヘッダ列のフォーカス枠を非表示
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
    boxShadow: 'none',
  },  
  '& .MuiDataGrid-filler': {
    backgroundColor: theme.palette.secondary.light,
  },
  '& .MuiDataGrid-root': {
    backgroundColor: theme.palette.secondary.light,
  },
  // セル選択時の枠を消す
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell--editing:focus, & .MuiDataGrid-cell--editing:focus-within': {
    outline: 'none',
    boxShadow: 'none',
    border: 'none',
  },
}));


function tabProps(index) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

// メモ検索ページ
const MemoSearchPage = (props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { control, register, handleSubmit, setValue, getValues, reset } = useForm();
  const [ condition, setCondition ] = useState(null);
  const [ users, setUsers ] = useState([]);
  const [ genres, setGenres ] = useState([]);
  const [ rows, setRows ] = useState([]);
  const [ processing, setProcessing ] = useState(false);
  const [ formReset, setFormReset ] = useState(false);
  const anchorRef = useRef(null);

  // メモリスト
  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 50
    },
    {
      field: 'user_full_name',
      headerName: '登録者',
      width: 150,
    },
    {
      field: 'target_date',
      headerName: '日付',
      width: 130,
    }, 
    {
      field: 'place',
      headerName: '場所',
      width: 200,
    },
    {
      field: 'genre_text',
      headerName: 'ジャンル',
      width: 200,
    },
    {
      field: 'contents',
      headerName: '本文',
      width: 300,
    },
  ];

  // 初期表示
  useEffect(() => {
    // 並列取得
    void Promise.all([
      // ユーザリスト取得
      fetchUserList(),
      // ジャンルリスト取得
      fetchGenreList(),
    ]);    
    // 検索条件初期化
    let form = {}
    // 検索条件取得
    const json = sessionStorage.getItem("memo_condition")
    if (json) { 
      // Jsonから連想配列へ変換
      form = JSON.parse(json)
    }
    // 検索条件項目数分
    for (let name in form) {
      // フォーム値へ設定
      setValue(name, form[name]);
    }
    // 検索条件設定
    setCondition(form)
  }, [])

  // 検索条件変更
  useEffect(() => {
    // 検索条件保存（Json）
    if (condition) sessionStorage.setItem("memo_condition", JSON.stringify(condition))
    // メモリスト取得
    fetchRows()
  }, [condition])

  // 検索条件リセット
  useEffect(() => {
    // リセット要求
    if (formReset) {
      // フォームのリセット（keywordsも空文字で明示的にクリア）
      reset({ keywords: "" })
      // リセット済み
      setFormReset(false)
    }
  }, [formReset])

  // ユーザリスト取得
  const fetchUserList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${userURL}/fullnames/`);
      // ユーザリスト設定
      setUsers(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ユーザリスト初期化
      setUsers([]);
    }
  }

  // ジャンルリスト取得
  const fetchGenreList = async () => {
    try {
      // API呼び出し
      //const data = await authJson(`${apiURL}/master/genres`);
      const res = await fetch("/memo_genres.json");
      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error('memo_genres.json unexpected format: ' + JSON.stringify(data));
      }
      // ジャンルリスト設定
      setGenres(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ジャンルリスト初期化
      setGenres([]);
    }
  }

  // メモリスト取得
  const fetchRows = async () => {
    // 検索条件が指定されていない
    if (condition === null) {
      return;
    }
    // キーワードの配列化
    if (condition.keywords && !Array.isArray(condition.keywords)) {
      condition.keywords = condition.keywords.split(/[\s]+/).filter(Boolean);
    }
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(condition))
    console.log(...body.entries());
    // メモ検索
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/memos`, { method: 'POST', body, actionName: 'SearchMemos' });
      // メモリスト設定
      setRows(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // メモリスト初期化
      setRows([]);
    }
  }

  // メモ検索
  const searchData = async (form) => {
    // 日付フォーマット
    form.target_date = toDateString(form.target_date);
    // キーワードの全角スペースを半角スペースへ全置換
    if (form.keywords && typeof form.keywords === 'string') {
      form.keywords = form.keywords.replace(/\u3000/g, ' ');
    }
    // ジャンルid配列がある場合
    if (Array.isArray(form.genre_ids)) {
      // id配列を名前配列に変換
      const genre_names = form.genre_ids.map(id => {
        const genre = genres.find(g => g.id === id);
        return genre ? genre.name : null;
      });
      // 配列のまま渡す
      form.genre_text = genre_names;
    }
    // 検索条件項目数分
    for (let name in form) {
      // フォーム値へ設定
      setValue(name, form[name]);
    }
    // 検索条件設定
    setCondition(form)
  }

  // タブ変更
  const handleTabChange = (event, newValue) => {
    if (newValue == 1) {
      // 地図へ
      navigate("view/map", { state: { rows: rows } });
    }
  };

  // クリアボタン
  const onFormClear = (e) => {
    // 検索条件リセット
    setFormReset(true)
  };

  // 新規作成ボタン
  const onNew = (e) => {
    // 詳細入力へ
    navigate("new");
  };

  // CSV出力
  const onOutputCsv = (e) => {
      const link = anchorRef.current
      if (!link) return

      // ヘッダ生成
      const headers = columns.map((col) => {
        if (col.headerName !== "") {
          return (col.headerName)
        }
      }).filter((header) => header != undefined)

      // データキーリスト生成
      const keys = columns.map((col) => {
        if (col.headerName !== "") {
          return (col.field)
        }
      }).filter((key) => key != undefined)

      // データ値リスト生成
      const values = rows.map((row) => {
        return (
          keys.map((key) => {
            let value = ""
            if (row[key] != null) {
              value = `"${row[key].toString()}"`
            }
            return (value)
          })
        )
      })

      // CSV文字列作成
      const table = [headers].concat(values)
      const csvText = table.map((str) => str.join(',')).join('\n')
      console.log(csvText)
    
      // CSVファイル名作成
      let timestamp = dayjs().format('YYYYMMDDHHmmss')
      const filename = `memos_${timestamp}.csv`

      // CSVファイル出力
      const bom  = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([bom, csvText], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.click()
  };

  // レンダリング
  return (
    <Container>
      <Card>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>メモ検索</Typography>
          <Typography variant="inherit" color="text.disabled">条件を指定して検索</Typography>
        </Box>
        <Box component="form" id="searchForm" onSubmit={handleSubmit(searchData)} sx={{ mt: 3, width: "100%" }}>
          <Grid container spacing={2}>
            <Grid size={2.4}>
              <CheckSelect id="user_id" label="登録者" placeholder="プルダウンから選択" value={getValues("user_id")} options={users} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <ThemeInputText id="keywords" label="キーワード(複数入力可能)" placeholder="フリーワードを入力" control={control}/>
            </Grid>
            <Grid size={2.4}>
              <SelectDate id="target_date" label="日付" placeholder="カレンダーから選択" control={control}/>
            </Grid>
            <Grid size={2.4}>
              <CheckSelect id="genre_ids" label="ジャンル" placeholder="プルダウンから選択" value={getValues("genre_ids")} options={genres} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <PrimaryButton variant="contained" sx={{mr: 1, minWidth: 100, width: "40%"}} onClick={onFormClear}>クリア</PrimaryButton>
              <SecondButton type="submit" form='searchForm' variant="contained" sx={{mr: 1, minWidth: 100, width: "40%"}}>検索</SecondButton>
            </Grid>
          </Grid>
        </Box>
      </Card>
      <Card>
        <Stack direction="column" spacing={2} sx={{ m: 1, width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>メモ一覧</Typography>
            <Typography variant="inherit" color="text.disabled">「メモ検索」でキーワードを指定することで絞り込みが可能です</Typography>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center">
            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={5} sx={{width: '100%'}}>
              <Typography variant="inherit" sx={{ fontWeight: 'normal' }}>表示方法</Typography>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: theme.palette.secondary.light }}>
                <Tabs value={0} onChange={handleTabChange}>
                  <Tab label="リスト" {...tabProps(0)} />
                  <Tab label="地図" {...tabProps(1)} />
                </Tabs>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <SecondButton variant="contained" onClick={onOutputCsv} sx={{display: 'none'}}>CSV出力</SecondButton>
              <BlackButton variant="contained" onClick={onNew} sx={{width: "200px"}}>新規メモ作成</BlackButton>
              <a ref={anchorRef} className='hidden'></a>
            </Stack>
          </Box>
          <Box sx={{ pr: 2, width: '100%', height: '500px' }}>
            <ThemeDataGrid
              rows={rows}
              columns={columns}
              slots={{ toolbar: SearchListToolbar }}
              localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
              disableColumnMenu
              onRowClick={(params) => {
                // 詳細入力へ
                navigate(`details/${params.row.id}`);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Stack>
      </Card>
    </Container>
  );
};

export default MemoSearchPage;